'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { CgSpinner } from 'react-icons/cg';
import { z } from 'zod';

import { Agreements } from '../../[id]/components/atoms/Agreements';
import Input from '../../contact/components/Input';
import { getSalesContactEmail } from '../../contact/lib/contact';

import { Checkbox } from '@components/Form';
import { toastError, toastSuccess } from '@components/root/NotificationProxy';
import { Link } from '@lib/navigation';
import { logger } from '@logger';
import { fetchApi } from '@utils/api/client';
import { IPRESSO_SOURCES } from '@utils/api/client/ipresso';
import { IpressoFetchResponse, IpressoPostBody } from '@utils/api/server/ipresso';
import { getIPressoAgreements } from '@utils/ipresso';

const schema = z.object({
  email: z.string().email(),
  firstName: z.string().min(2).max(255),
  lastName: z.string().min(2).max(255),
  company: z.string().optional(),
  phone: z.string().optional(),
  campaignType: z.string().optional(),
  message: z.string().min(9),
  marketingAgreement: z.boolean().refine((value) => value === true),
  commercialInside: z.boolean().optional(),
  commercialOutside: z.boolean().optional(),
});

const schemaDE = z.object({
  email: z.string().email(),
  firstName: z.string().min(2).max(255),
  lastName: z.string().min(2).max(255),
  company: z.string().optional(),
  phone: z.string().optional(),
  campaignType: z.string().optional(),
  message: z.string().min(9),
  agreementDE: z.boolean().refine((value) => value === true),
});

const isDESchema = (
  _schema: z.infer<typeof schema> | z.infer<typeof schemaDE>,
): _schema is z.infer<typeof schemaDE> => {
  return 'agreementDE' in _schema;
};

export default function SalesContactForm() {
  const locale = useLocale();
  const t = useTranslations('main');
  const tForm = useTranslations('campaign.order.form');
  const tTypes = useTranslations('campaign.order.campaignTypes');

  const usedSchema = locale === 'de' ? schemaDE : schema;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<z.infer<typeof usedSchema>>({
    resolver: zodResolver(usedSchema),
  });

  const contactSubmit = handleSubmit(async (data) => {
    const agreements = getIPressoAgreements(
      isDESchema(data)
        ? { marketingAgreement: data.agreementDE }
        : {
            marketingAgreement: data.marketingAgreement,
            commercialInside: data.commercialInside,
            commercialOutside: data.commercialOutside,
          },
    );

    try {
      const bodyObject: IpressoPostBody = {
        contactData: { email: data.email },
        lang: {
          agreement: locale,
        },
        agreementData: agreements,
        source: IPRESSO_SOURCES.SALES,
      };

      await fetchApi<IpressoFetchResponse>('/campaign/edit', {
        method: 'POST',
        noAuth: true,
        body: JSON.stringify(bodyObject),
      });

      await fetchApi('/contact', {
        method: 'POST',
        noAuth: true,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient: getSalesContactEmail(locale),
          name: `${data.firstName} ${data.lastName}`,
          email: data.email,
          consent: isDESchema(data) ? data.agreementDE : data.marketingAgreement,
          message: data.message,
          company: data.company,
          phone: data.phone,
          campaignType: data.campaignType,
          locale,
          source: 'sales',
        }),
      }).then(() => {
        toastSuccess(t('message.success.contact'));
      });
    } catch (e) {
      logger.error(e, 'Sales contact form %s', (e as any)?.message);
      toastError(t('message.account.errors.emailResendFailure'));
    }
  });

  return (
    <form onSubmit={contactSubmit} className="flex-1 space-y-4 p-6 text-left md:p-8">
      <div className="mb-3">
        <Input
          className="mb-1"
          label={t('label.name')}
          error={!!errors.firstName}
          {...register('firstName')}
        />
        {errors.firstName && <p className="text-xs text-red-500">{errors.firstName.message}</p>}
      </div>

      <div className="mb-3">
        <Input label={t('label.surname')} className="mb-1" error={!!errors.lastName} {...register('lastName')} />
        {errors.lastName && <p className="text-xs text-red-500">{errors.lastName.message}</p>}
      </div>

      <div className="mb-3">
        <Input className="mb-1" type="email" label={t('label.email')} error={!!errors.email} {...register('email')} />
        {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
      </div>

      <div className="mb-3">
        <Input label={tForm('company')} className="mb-1" error={!!errors.company} {...register('company')} />
        {errors.company && <p className="text-xs text-red-500">{errors.company.message}</p>}
      </div>

      <div className="mb-3">
        <Input
          label={tForm('phone')}
          type="tel"
          className="mb-1"
          error={!!errors.phone}
          {...register('phone')}
        />
        {errors.phone && <p className="text-xs text-red-500">{errors.phone.message}</p>}
      </div>

      <div className="mb-3">
        <div className="relative">
          <label className="absolute -top-2 left-3 bg-white px-1 text-sm text-xs text-premium-dark dark:bg-premium-darker dark:text-white">
            {tForm('campaignType')}
          </label>
          <select
            {...register('campaignType')}
            className="w-full rounded-md border-2 border-premium-dark border-opacity-60 bg-white px-4 py-2 text-sm outline-none focus:border-premium-500 dark:border-gray-650 dark:bg-premium-darker dark:text-white"
          >
            <option value="">{tForm('campaignTypePlaceholder')}</option>
            <option value="banners">{tTypes('banners')}</option>
            <option value="sponsored">{tTypes('sponsored')}</option>
            <option value="combo">{tTypes('combo')}</option>
            <option value="other">{tTypes('other')}</option>
          </select>
        </div>
        {errors.campaignType && <p className="text-xs text-red-500">{errors.campaignType.message}</p>}
      </div>

      <div className="relative mb-3">
        <label className="absolute -top-2 left-3 bg-white px-1 text-sm text-xs text-premium-dark dark:bg-premium-darker dark:text-white">
          {tForm('message')}
        </label>
        <textarea
          className="mb-1 h-32 w-full rounded-md border-2 border-premium-dark border-opacity-60 px-4 py-2 placeholder:text-gray-500 dark:border-gray-650 dark:bg-premium-darker dark:text-white"
          {...register('message')}
        />
        {errors.message && <p className="text-xs text-red-500">{errors.message.message}</p>}
      </div>

      {locale !== 'de' ? (
        <Agreements commercialInsideRequired={false} setValue={setValue} watch={watch} register={register} errors={errors} />
      ) : (
        <Checkbox
          label={
            <span>
              Ich habe die&nbsp;
              <Link className="text-premium-500 underline" href="/rules">
                Nutzungsbedingungen
              </Link>
              &nbsp;gelesen und zur Kenntnis genommen
            </span>
          }
          wrapperClassName="mb-2 lg:mb-3"
          {...register('agreementDE')}
        />
      )}

      <div className="flex flex-col items-center text-center lg:text-left">
        <button
          disabled={isSubmitting}
          type="submit"
          className={classNames(
            'my-4 inline-flex w-full items-center justify-center rounded-full bg-[#F7931E] px-16 py-3 font-bold text-white transition-colors hover:bg-[#e67c00] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-[#F7931E] lg:my-8 lg:w-[unset]',
          )}
        >
          {isSubmitting && <CgSpinner className="mr-1 animate-spin" />}
          {t('label.buttons.sendMessage')}
        </button>
      </div>
    </form>
  );
}
